// src/fleet-stats/analytics/buildAttentionList.js
//
// Merges robot-level insight findings (buildInsights) with low Robot Readiness
// Index scores (robotHealth) into one exception list. The key principle is
// severity ≠ novelty: what ranks high is what CHANGED recently, not what has
// been broken the longest.
//
// Tiers:
//   kind "infield"  — robot has data in the last 7 days but is misbehaving.
//                     Actionable (critical/warning) only if it drove within the
//                     last 21 days; a robot quiet longer than that is a
//                     STANDING issue → info tier.
//   kind "offline"  — no data at all for >7 days. Recently lost (≤30d) is a
//                     warning; dark longer than that is info-tier inventory.
import { daysBetween } from "../utils/date.js";

const REASON_WEIGHT = { drop: 3, idle: 2, utilization: 1 };
const STALE_OFFLINE_DAYS = 30;
const STANDING_QUIET_DAYS = 21;

function reasonText(type, r) {
    switch (type) {
        case "drop":        return `Driving down ${r.pct}% vs its 3-week norm`;
        case "idle":        return "Powered on but not driving for 7+ days";
        case "utilization": return `Only ${r.util}% of powered-on time spent driving`;
        default:            return null;
    }
}

export function buildAttentionList(findings, healthMap, rows, maxDate) {
    // robotId → display meta + last day the robot actually drove
    const meta = new Map();
    const lastDrove = new Map();
    for (const r of rows) {
        if (!meta.has(r.robotId)) meta.set(r.robotId, { robotName: r.robotName, client: r.client, siteName: r.siteName });
        if (r.distance_m > 0 || r.moving_time_s > 0) {
            const prev = lastDrove.get(r.robotId);
            if (!prev || r.date > prev) lastDrove.set(r.robotId, r.date);
        }
    }

    const byRobot = new Map();
    const ensure = (robotId, fallback = {}) => {
        let e = byRobot.get(robotId);
        if (!e) {
            const m = meta.get(robotId) || {};
            e = {
                robotId,
                robotName: fallback.robotName || m.robotName || robotId,
                client:    fallback.client    || m.client    || "Unknown",
                siteName:  fallback.siteName  || m.siteName  || "",
                reasons: [],
                weight: 0,
                offline: false,
                standing: false,
                lastDate: null,
                quietDays: null,
                rri: null, rriLabel: null, rriColor: null,
            };
            byRobot.set(robotId, e);
        }
        return e;
    };

    for (const f of findings || []) {
        if (!Array.isArray(f.robots)) continue;
        for (const r of f.robots) {
            if (!r.robotId) continue;
            const e = ensure(r.robotId, r);
            if (f.type === "offline") {
                const daysDark = maxDate && r.lastDate ? daysBetween(r.lastDate, maxDate) : null;
                e.offline = true;
                e.lastDate = r.lastDate;
                e.reasons.push({ type: "offline", text: `No data since ${r.lastDate}${daysDark !== null ? ` (${daysDark}d)` : ""}` });
                e.weight = Math.max(e.weight, daysDark !== null && daysDark <= STALE_OFFLINE_DAYS ? 2 : 1);
            } else {
                const w = REASON_WEIGHT[f.type];
                if (!w) continue;
                const text = reasonText(f.type, r);
                if (text) e.reasons.push({ type: f.type, text });
                e.weight = Math.max(e.weight, w);
            }
        }
    }

    // Low readiness: "At risk" and "Inactive" bands (RRI < 40). Higher bands
    // only annotate robots already flagged by a finding.
    if (healthMap) {
        for (const [robotId, h] of healthMap.entries()) {
            if (h.rri === null || h.rri >= 40) {
                const listed = byRobot.get(robotId);
                if (listed && h.rri !== null) { listed.rri = h.rri; listed.rriLabel = h.label; listed.rriColor = h.color; }
                continue;
            }
            const e = ensure(robotId);
            e.rri = h.rri; e.rriLabel = h.label; e.rriColor = h.color;
            e.reasons.push({ type: "rri", text: `Readiness ${h.rri}/100 — ${h.label.toLowerCase()}` });
            e.weight = Math.max(e.weight, h.rri < 20 ? 3 : 2);
        }
    }

    // ── Post-process: kind, reason de-dup, standing demotion ────────────────
    for (const e of byRobot.values()) {
        e.kind = e.offline ? "offline" : "infield";

        if (e.kind === "offline") {
            // "Driving down 100%" next to "no data for 10 days" is noise
            e.reasons = e.reasons.filter((r) => r.type === "offline");
            continue;
        }

        // The RRI badge already carries the score — drop its text reason when
        // a concrete reason exists
        const concrete = e.reasons.filter((r) => r.type !== "rri");
        if (concrete.length > 0) e.reasons = concrete;

        const ld = lastDrove.get(e.robotId) || null;
        e.quietDays = ld && maxDate ? daysBetween(ld, maxDate) : null;

        if (!ld || e.quietDays > STANDING_QUIET_DAYS) {
            // Quiet longer than the baseline window: this isn't news anymore
            e.standing = true;
            e.weight = Math.min(e.weight, 1);
            e.reasons = [{
                type: "standing",
                text: ld ? `Hasn't driven since ${ld} (${e.quietDays}d) — standing issue` : "Powered on but never driven — standing issue",
            }];
        } else if (e.quietDays !== null && e.quietDays > 1) {
            e.reasons.push({ type: "context", text: `Last drove ${e.quietDays} days ago` });
        }
    }

    const severityOf = (w) => (w >= 3 ? "critical" : w === 2 ? "warning" : "info");

    return Array.from(byRobot.values())
        .filter((e) => e.reasons.length > 0)
        .map((e) => ({ ...e, severity: severityOf(e.weight) }))
        .sort((a, b) => {
            if (b.weight !== a.weight) return b.weight - a.weight;
            // Scored robots first (worst RRI up top), then unscored offline
            // ones by how recently they disappeared.
            const ar = a.rri ?? Infinity, br = b.rri ?? Infinity;
            if (ar !== br) return ar - br;
            if (a.lastDate && b.lastDate && a.lastDate !== b.lastDate) return b.lastDate.localeCompare(a.lastDate);
            return a.robotName.localeCompare(b.robotName);
        });
}
